import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/ui-helpers";
import { PortalLayout } from "@/components/PortalLayout";

import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClients from "@/pages/AdminClients";
import AdminClientDetail from "@/pages/AdminClientDetail";
import AdminStaff from "@/pages/AdminStaff";
import AdminApplications from "@/pages/AdminApplications";
import ApplicationDetail from "@/pages/ApplicationDetail";
import Leads from "@/pages/Leads";
import ChecklistTemplates from "@/pages/ChecklistTemplates";
import Appointments from "@/pages/Appointments";
import BookAppointment from "@/pages/BookAppointment";
import Billing from "@/pages/Billing";
import Communications from "@/pages/Communications";
import TeamMessages from "@/pages/TeamMessages";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import DocumentsQueue from "@/pages/DocumentsQueue";
import { FEATURES } from "@/lib/features";
import TasksBoard from "@/pages/TasksBoard";
import AdminExpenses from "@/pages/AdminExpenses";
import SignRetainer from "@/pages/SignRetainer";
import Esign from "@/pages/Esign";
import EsignBuilder from "@/pages/EsignBuilder";
import PublicEsign from "@/pages/PublicEsign";
import PublicPay from "@/pages/PublicPay";
import PublicBooking from "@/pages/PublicBooking";
import PublicQuestionnaire from "@/pages/PublicQuestionnaire";
import ShortTermCourses from "@/pages/ShortTermCourses";

// Mobile companion app (isolated under /m — does not touch the web CRM routes above)
import { MobileLayout, MobileGuard } from "@/mobile/MobileLayout";
import MobileLogin from "@/mobile/MobileLogin";
import MobileHome from "@/mobile/MobileHome";
import MobileChat from "@/mobile/MobileChat";
import MobileChatThread from "@/mobile/MobileChatThread";
import MobileClients from "@/mobile/MobileClients";
import MobileClientDetail from "@/mobile/MobileClientDetail";
import MobileTasks from "@/mobile/MobileTasks";
import MobileExpenses from "@/mobile/MobileExpenses";
import MobileNotifications from "@/mobile/MobileNotifications";
import MobileAccount from "@/mobile/MobileAccount";

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loader label="Loading..." />;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user && user !== false && user.role !== "client") return <Navigate to="/admin" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      {/* Public client e-signature link (no login) */}
      <Route path="/sign/:token" element={<SignRetainer />} />
      <Route path="/esign/:token" element={<PublicEsign />} />
      {/* Public client "Pay now" page (no login) */}
      <Route path="/pay/:token" element={<PublicPay />} />
      {/* Public online appointment booking (no login) — shareable & embeddable */}
      <Route path="/book-appointment" element={<PublicBooking />} />
      <Route path="/questionnaire/:token" element={<PublicQuestionnaire />} />

      {/* Internal CRM (Admin / Staff only) */}
      <Route path="/admin" element={<RoleRoute roles={["admin", "staff"]}><PortalLayout /></RoleRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:id" element={<AdminClientDetail />} />
        <Route path="applications" element={<AdminApplications />} />
        <Route path="applications/:id" element={<ApplicationDetail />} />
        <Route path="checklists" element={FEATURES.documents ? <ChecklistTemplates /> : <Navigate to="/admin" replace />} />
        <Route path="calendar" element={<Navigate to="/admin/appointments" replace />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="book" element={<BookAppointment />} />
        <Route path="documents" element={FEATURES.documents ? <DocumentsQueue /> : <Navigate to="/admin" replace />} />
        <Route path="tasks" element={<TasksBoard />} />
        <Route path="billing" element={<Billing />} />
        <Route path="short-term" element={<ShortTermCourses />} />
        <Route path="esign" element={<Esign />} />
        <Route path="esign/new" element={<EsignBuilder />} />
        <Route path="esign/:id" element={<EsignBuilder />} />
        <Route path="expenses" element={<AdminExpenses />} />
        <Route path="communications" element={<Communications />} />
        <Route path="messages" element={<TeamMessages />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Mobile companion app (staff/admin) — installable PWA, isolated under /m */}
      <Route path="/m/login" element={<MobileLogin />} />
      <Route path="/m" element={<MobileGuard><MobileLayout /></MobileGuard>}>
        <Route index element={<MobileHome />} />
        <Route path="chat" element={<MobileChat />} />
        <Route path="chat/:cid" element={<MobileChatThread />} />
        <Route path="clients" element={<MobileClients />} />
        <Route path="clients/:id" element={<MobileClientDetail />} />
        <Route path="tasks" element={<MobileTasks />} />
        <Route path="expenses" element={<MobileExpenses />} />
        <Route path="notifications" element={<MobileNotifications />} />
        <Route path="account" element={<MobileAccount />} />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
